/**
 * ProjectileWeaponComponent - fires Projectile actors with configurable options.
 * Spawns projectiles with customizable speed, damage, and collision behavior.
 */

import { Projectile } from '../../actors/Projectile.js';
import { EngineClass } from '../../systems/ClassRegistry.js';
import { Delegate } from '../../utils/Delegate.js';

import { BaseWeaponComponent } from './BaseWeaponComponent.js';

import type { BaseWeaponOptions } from './BaseWeaponComponent.js';
import type { IEquipment } from './Equipment.js';
import type { ProjectileOptions } from '../../actors/Projectile.js';
import type { OptionalSome } from '../../utils/Utils.js';
import type * as THREE from 'three';

export type WeaponProjectileOptions = OptionalSome<ProjectileOptions, 'position' | 'direction'>;

export interface ProjectileWeaponOptions extends BaseWeaponOptions {
  projectileOptions?: WeaponProjectileOptions;
  /**
   * Optional class to use for the projectile.
   * If not provided, the default Projectile class will be used.
   */
  projectileClass?: (new () => Projectile) & {
    create(options?: ProjectileOptions): Projectile;
  };
  /**
   * Optional offset from the weapon component's position for the projectile spawn.
   */
  projectileSpawnOffset?: THREE.Vector3;
}

export type ProjectileFiredDelegate = Delegate<[Projectile], void>;

@EngineClass('ProjectileWeaponComponent')
export class ProjectileWeaponComponent extends BaseWeaponComponent implements IEquipment {


  protected projectileOptions?: WeaponProjectileOptions;
  protected projectileClass: (new () => Projectile) & {
    create(options?: ProjectileOptions): Projectile;
  } = Projectile;
  protected projectileSpawnOffset?: THREE.Vector3;

  public readonly onProjectileFired: ProjectileFiredDelegate = new Delegate<[Projectile], void>();

  public override initialize(options?: ProjectileWeaponOptions): void {
    super.initialize(options);
    this.projectileOptions = options?.projectileOptions ?? this.projectileOptions;
    this.projectileClass = options?.projectileClass ?? this.projectileClass;
    this.projectileSpawnOffset = options?.projectileSpawnOffset ?? this.projectileSpawnOffset;
  }



  protected override doFire(): void {
    // Guard against firing without ammo
    if (!this.canFire() || !this.projectileOptions) {
      return;
    }

    let { position, direction } = this.getFirePositionAndDirection();

    // Apply target direction if available
    const targetDirection = this.getTargetDirection();
    if (targetDirection) {
      direction = targetDirection;
    }

    const projectile = this.projectileClass.create({
      ...this.projectileOptions,
      // honor the provided position and direction if they exist
      position: this.projectileOptions.position ?? position,
      direction: this.projectileOptions.direction ?? direction,
      // Pass the owner actor so the projectile won't hit its owner
      owner: this.getActor() ?? undefined,
    });
    this.getWorld()?.addActors(projectile);
    this.onProjectileFired.invoke(projectile);

    // Handle weapon effects and ammo
    this.consumeAmmo();
    this.playFireSound();
    this.applyRecoilEffects();
  }

  protected override getFirePositionAndDirection(): { position: THREE.Vector3, direction: THREE.Vector3 } {
    const { position, direction } = super.getFirePositionAndDirection();

    // Apply projectile-specific spawn offset if provided
    if (this.projectileSpawnOffset) {
      position.add(this.projectileSpawnOffset.clone().applyQuaternion(this.getWorldQuaternion()));
    }

    return { position, direction };
  }

  //====================================================================================================
  // Editor Functions
  //====================================================================================================

  public override getEditorClassIcon(): string | null {
    return 'Icon_ProjectileWeapon';
  }
}
